import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";

import { api, usePoll } from "../api";
import type { Device, Recording } from "../api";
import deviceThumb from "../assets/device-thumb.webp";
import { Icon } from "../components/icons";
import { QuietGap, RecordingList, RecordingProgress, TrainingPanel } from "../components/recording";
import { fmtAgo } from "../format";

const SECTIONS = [
  { id: "general", label: "General", icon: <Icon.Gear size={22} /> },
  { id: "audio", label: "Audio & detection", icon: <Icon.Wave size={22} /> },
  { id: "network", label: "Network", icon: <Icon.Wifi /> },
  { id: "storage", label: "Storage", icon: <Icon.Storage /> },
  { id: "integrations", label: "Integrations", icon: <Icon.Link /> },
  { id: "updates", label: "Updates", icon: <Icon.Download /> },
];

/** A device's settings — the page behind "Settings" on the Devices list. */
export function DeviceSettings() {
  const { deviceId, section = "general" } = useParams();
  const { data: device, error, reload } = usePoll<Device>(`/api/devices/${deviceId}`, 10_000);
  const recordings = usePoll<Recording[]>(`/api/devices/${deviceId}/test-recordings`);
  const [trainingId, setTrainingId] = useState<string | null>(null);
  const [testId, setTestId] = useState<string | null>(null);
  const [showLatest, setShowLatest] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (error) return <p className="error">{error}</p>;
  if (!device) return <div className="spinner" />;

  async function train(kind: "positive" | "negative") {
    setActionError(null);
    try {
      const r = await api.post<{ training_session_id: string }>(`/api/devices/${deviceId}/train`, { kind });
      setTestId(null);
      setTrainingId(r.training_session_id);
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  async function testMic() {
    setActionError(null);
    try {
      const r = await api.post<{ training_session_id: string }>(`/api/devices/${deviceId}/test-recording`);
      setTrainingId(null);
      setShowLatest(false);
      setTestId(r.training_session_id);
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  const busy = trainingId !== null || testId !== null;
  const actions = { train, testMic, busy, online: device.online };

  return (
    <section className="settings-page">
      <aside className="settings-nav">
        <Link to="/devices" className="back"><Icon.ArrowLeft /> Back to devices</Link>
        <nav>
          {SECTIONS.map((s) => (
            <NavLink key={s.id} to={`/devices/${device.id}/${s.id}`}
                     className={() => (section === s.id ? "active" : "")}>
              {s.icon}{s.label}
            </NavLink>
          ))}
          <hr />
          <NavLink to={`/devices/${device.id}/advanced`} className={() => (section === "advanced" ? "active" : "")}>
            <Icon.Sliders />Advanced
          </NavLink>
        </nav>
      </aside>

      <div className="settings-main">
        <DeviceHeader device={device} onRenamed={reload} actions={actions} />

        {actionError && <p className="error">{actionError}</p>}
        {trainingId && (
          <TrainingPanel id={trainingId} onDone={() => { setTrainingId(null); reload(); }} />
        )}
        {testId && (
          <div className="panel panel-col">
            <h3>Test recording</h3>
            <RecordingProgress
              id={testId}
              onDone={() => { setTestId(null); setShowLatest(true); recordings.reload(); }}
            />
          </div>
        )}
        {showLatest && recordings.data?.[0] && (
          <div className="panel panel-col">
            <div className="panel-row">
              <h3>Latest test recording</h3>
              <button className="link" onClick={() => setShowLatest(false)}>Close</button>
            </div>
            <RecordingList items={recordings.data.slice(0, 1)} />
            <Link to={`/devices/${device.id}/audio`} className="small">All test recordings →</Link>
          </div>
        )}

        {section === "general" && <General device={device} onChanged={reload} actions={actions} />}
        {section === "audio" && (
          <Audio device={device} onChanged={reload} recordings={recordings.data ?? []} actions={actions} />
        )}
        {section === "network" && <Network device={device} />}
        {section === "storage" && (
          <Placeholder icon={<Icon.Storage />} title="Storage"
                       text="See how much space recordings use on the device and in your account, and how long they're kept." />
        )}
        {section === "integrations" && (
          <Placeholder icon={<Icon.Link />} title="Integrations"
                       text="Send incidents to your phone, email or smart-home system." />
        )}
        {section === "updates" && <Updates device={device} />}
        {section === "advanced" && <Advanced device={device} />}
      </div>
    </section>
  );
}

type Actions = {
  train: (kind: "positive" | "negative") => void;
  testMic: () => void;
  busy: boolean;
  online: boolean;
};

/* --- Header ------------------------------------------------------------------ */

function DeviceHeader({ device, onRenamed, actions }: { device: Device; onRenamed: () => void; actions: Actions }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="panel settings-head">
      <img className="settings-thumb" src={deviceThumb} alt="" width={150} />
      <div className="settings-head-text">
        {editing ? (
          <NameForm device={device} onDone={() => { setEditing(false); onRenamed(); }} autoFocus />
        ) : (
          <h1>
            {device.name ?? device.id}
            <button className="icon-button" aria-label="Rename device" onClick={() => setEditing(true)}><Icon.Pencil /></button>
          </h1>
        )}
        <div className="muted">{device.id} · firmware {device.fw_version ?? "—"}</div>
        <div className="settings-head-status">
          <OnlineText device={device} />
          <span className="muted">Last heard {device.last_seen_at ? fmtAgo(device.last_seen_at) : "never"}</span>
        </div>
      </div>
      <div className="settings-head-actions">
        <button className="ghost" disabled={!actions.online || actions.busy} onClick={actions.testMic}>
          <Icon.Wave size={20} /> Test microphone
        </button>
        <button className="cta cta-small" disabled={!actions.online || actions.busy} onClick={() => actions.train("positive")}>
          <Icon.Wave size={20} /> Train sound
        </button>
      </div>
    </div>
  );
}

function OnlineText({ device }: { device: Device }) {
  return (
    <span className={`online-text ${device.online ? "on" : "off"}`}>
      <span className="dot" />{device.online ? "Listening" : "Offline"}
    </span>
  );
}

function NameForm({ device, onDone, autoFocus = false }: { device: Device; onDone: () => void; autoFocus?: boolean }) {
  const [name, setName] = useState(device.name ?? "");
  const [saved, setSaved] = useState(false);
  useEffect(() => setName(device.name ?? ""), [device.name]);
  async function save(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && name.trim() !== device.name) {
      await api.patch(`/api/devices/${device.id}`, { name: name.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
    onDone();
  }
  return (
    <form className="name-form" onSubmit={save}>
      <input value={name} maxLength={100} autoFocus={autoFocus} aria-label="Device name"
             onChange={(e) => setName(e.target.value)} />
      {name.trim() !== (device.name ?? "") && <button className="cta cta-small">Save</button>}
      {saved && <span className="good small"><Icon.Check /> Saved</span>}
    </form>
  );
}

/* --- General ------------------------------------------------------------------ */

function General({ device, onChanged, actions }: { device: Device; onChanged: () => void; actions: Actions }) {
  return (
    <div className="settings-grid">
      <div className="settings-col">
        <div className="panel panel-col">
          <h3>Device information</h3>
          <dl className="info-rows">
            <dt>Device name</dt>
            <dd><NameForm device={device} onDone={onChanged} /></dd>
            <dt>Serial number</dt>
            <dd><CopyText text={device.id} /></dd>
            <dt>Firmware version</dt>
            <dd className="row-split">
              {device.fw_version ?? "—"}
              {/* Placeholder: there is no update check yet, so this does not claim "up to date". */}
              <span className="soon-badge">Update checks soon</span>
            </dd>
            <dt>Status</dt>
            <dd><OnlineText device={device} /></dd>
            <dt>Last heard</dt>
            <dd>{device.last_seen_at ? fmtAgo(device.last_seen_at) : "never"}</dd>
          </dl>
        </div>

        <ActionsCard device={device} actions={actions} />
      </div>

      <div className="settings-col">
        <div className="panel panel-col placeholder-panel">
          <PanelTitle icon={<Icon.Mic />} title="Microphone settings" soon>
            Adjust how your device listens for sounds.
          </PanelTitle>
          <label className="field-label">Microphone sensitivity</label>
          <div className="slider-row">
            <input type="range" min={0} max={100} defaultValue={70} disabled aria-label="Microphone sensitivity" />
            <span>70%</span>
          </div>
          <p className="muted small">Higher sensitivity can detect quieter sounds but may pick up more background noise.</p>
          <label className="field-label">Detection mode</label>
          <label className="radio">
            <input type="radio" name="mode" defaultChecked disabled />
            <span><strong>Bark detection (recommended)</strong><span className="muted small">Optimised for dog barking and similar sounds</span></span>
          </label>
          <label className="radio">
            <input type="radio" name="mode" disabled />
            <span><strong>General noise detection</strong><span className="muted small">Detect a wider range of sounds</span></span>
          </label>
        </div>

        <div className="panel panel-col placeholder-panel">
          <PanelTitle icon={<Icon.Pin />} title="Device location" soon>
            Set where this device is located to help you keep track.
          </PanelTitle>
          <select disabled defaultValue="living" aria-label="Device location">
            <option value="living">Living Room</option>
          </select>
        </div>

        <div className="panel panel-col placeholder-panel">
          <PanelTitle icon={<Icon.Clock />} title="Device time" soon>
            Ensure your device has the correct time for accurate diary entries.
          </PanelTitle>
          <div className="inline-field">
            <label className="field-label">Time zone</label>
            <select disabled defaultValue="london" aria-label="Time zone">
              <option value="london">(UTC+00:00) London</option>
            </select>
          </div>
          <p className="muted small">The device keeps UTC from the internet; the diary shows UK time.</p>
        </div>
      </div>
    </div>
  );
}

function ActionsCard({ device, actions }: { device: Device; actions: Actions }) {
  const navigate = useNavigate();
  const [restart, setRestart] = useState<"idle" | "sent" | "error">("idle");
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function doRestart() {
    try {
      await api.post(`/api/devices/${device.id}/restart`);
      setRestart("sent");
    } catch {
      setRestart("error");
    }
  }

  async function remove() {
    await api.delete(`/api/devices/${device.id}`);
    navigate("/devices");
  }

  return (
    <div className="panel panel-col">
      <h3>Actions</h3>
      <p className="muted">Test your device, retrain detection or restart if needed.</p>
      <div className="action-grid">
        <div>
          <button className="ghost" disabled={!actions.online || actions.busy} onClick={actions.testMic}>
            <Icon.Wave size={20} /> Test microphone
          </button>
          <p className="muted small">Record 10 seconds and hear what it hears</p>
        </div>
        <div>
          <button className="ghost" disabled={!actions.online || actions.busy} onClick={() => actions.train("positive")}>
            <Icon.Wave size={20} /> Train sound
          </button>
          <p className="muted small">Help BotherLog learn your target sound</p>
        </div>
        <div>
          <button className="ghost" disabled={!device.online || restart === "sent"} onClick={doRestart}>
            <Icon.Restart /> Restart device
          </button>
          <p className="muted small">
            {restart === "sent" ? "Restarting — back in about a minute" : restart === "error" ? "Couldn't send the restart" : "Reboots the device (usually takes 1–2 mins)"}
          </p>
        </div>
      </div>
      <div className="remove-row">
        {confirmRemove ? (
          <>
            <button className="danger-button" onClick={remove}><Icon.Trash /> Yes, remove it</button>
            <button className="link" onClick={() => setConfirmRemove(false)}>Cancel</button>
          </>
        ) : (
          <button className="danger-button" onClick={() => setConfirmRemove(true)}><Icon.Trash /> Remove device</button>
        )}
        <p className="muted small">This will remove the device from your account. Your diary data will be kept.</p>
      </div>
    </div>
  );
}

/* --- Audio & detection --------------------------------------------------------- */

function Audio({ device, onChanged, recordings, actions }: {
  device: Device; onChanged: () => void; recordings: Recording[]; actions: Actions;
}) {
  const profile = device.profile;
  const trained = Boolean(profile?.current_version);
  return (
    <div className="settings-stack">
      <div className="panel panel-col">
        <PanelTitle icon={<Icon.Wave size={24} />} title={trained ? `Watching for: ${profile!.label}` : "Not trained yet"}>
          {trained
            ? "BotherLog only logs the sound it was trained on."
            : "Press Train sound while the dog is barking, so BotherLog learns that dog's pitch and loudness."}
        </PanelTitle>
        {trained && profile!.summary && (
          <p className="muted">
            Pitch {profile!.summary.freq_hz[0]}–{profile!.summary.freq_hz[1]} Hz · level{" "}
            {profile!.summary.level_dbfs[0]} to {profile!.summary.level_dbfs[1]} dBFS
            {profile!.summary.negatives ? ` · ignoring ${profile!.summary.negatives} other sound(s)` : ""}
            {" · "}{profile!.on_device ? "running on the device" : "waiting for the device to pick it up"}
          </p>
        )}
        <div className="panel-actions">
          <button className="cta cta-small" disabled={!actions.online || actions.busy} onClick={() => actions.train("positive")}>
            {trained ? "Retrain" : "Train sound"}
          </button>
          {trained && (
            <button className="link" disabled={!actions.online || actions.busy} onClick={() => actions.train("negative")}>
              It's logging the wrong dog — record what to ignore
            </button>
          )}
        </div>
      </div>

      {trained && <QuietGap device={device} onSaved={onChanged} />}

      <div className="panel panel-col">
        <div className="panel-row">
          <PanelTitle icon={<Icon.Mic />} title="Test recordings">
            Hear exactly what the microphone picks up. Doesn't affect what it's trained to log.
          </PanelTitle>
          <button className="ghost" disabled={!actions.online || actions.busy} onClick={actions.testMic}>Record 10 seconds</button>
        </div>
        <RecordingList items={recordings} />
      </div>
    </div>
  );
}

/* --- Network / Updates / Advanced / placeholders -------------------------------- */

function Network({ device }: { device: Device }) {
  const rssi = device.status?.rssi;
  const quality = rssi == null ? "—" : rssi >= -60 ? "Strong" : rssi >= -70 ? "Good" : rssi >= -80 ? "Weak" : "Very weak";
  return (
    <div className="settings-stack">
      <div className="panel panel-col">
        <PanelTitle icon={<Icon.Wifi />} title="Network">Connection between the device and BotherLog.</PanelTitle>
        <dl className="info-rows">
          <dt>Connection</dt><dd><OnlineText device={device} /></dd>
          <dt>Wi-Fi signal</dt><dd>{rssi == null ? "—" : `${quality} (${rssi} dBm)`}</dd>
          <dt>Last heard</dt><dd>{device.last_seen_at ? fmtAgo(device.last_seen_at) : "never"}</dd>
        </dl>
      </div>
      <Placeholder icon={<Icon.Wifi />} title="Change Wi-Fi network"
                   text="Switch networks from here. For now, use the device's setup mode: it starts its own BotherLog-XXXX Wi-Fi when it can't connect." />
    </div>
  );
}

function Updates({ device }: { device: Device }) {
  return (
    <div className="settings-stack">
      <div className="panel panel-col">
        <PanelTitle icon={<Icon.Download />} title="Firmware">Updates are installed over Wi-Fi by BotherLog.</PanelTitle>
        <dl className="info-rows">
          <dt>Installed version</dt><dd>{device.fw_version ?? "—"}</dd>
        </dl>
      </div>
      <Placeholder icon={<Icon.Download />} title="Automatic updates"
                   text="Check for new firmware and choose when updates install." />
    </div>
  );
}

function Advanced({ device }: { device: Device }) {
  const s = device.status;
  return (
    <div className="settings-stack">
      <div className="panel panel-col">
        <PanelTitle icon={<Icon.Sliders />} title="Diagnostics">Read-only details reported by the device.</PanelTitle>
        <dl className="info-rows">
          <dt>Device ID</dt><dd><CopyText text={device.id} /></dd>
          <dt>Background level</dt><dd>{s?.noise_floor_dbfs != null ? `${s.noise_floor_dbfs.toFixed(1)} dBFS` : "—"}</dd>
          <dt>Uptime</dt><dd>{s?.uptime_s != null ? fmtUptime(s.uptime_s) : "—"}</dd>
          <dt>Firmware</dt><dd>{device.fw_version ?? "—"}</dd>
        </dl>
      </div>
      <Placeholder icon={<Icon.Sliders />} title="Advanced settings"
                   text="Detection thresholds, recording length and other expert options." />
    </div>
  );
}

function fmtUptime(s: number): string {
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
  return `${Math.floor(s / 86400)} days`;
}

function Placeholder({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="panel panel-col placeholder-panel">
      <PanelTitle icon={icon} title={title} soon>{text}</PanelTitle>
    </div>
  );
}

function PanelTitle({ icon, title, soon = false, children }: {
  icon: ReactNode; title: string; soon?: boolean; children: ReactNode;
}) {
  return (
    <div className="panel-title">
      <span className="panel-title-icon">{icon}</span>
      <span>
        <h3>{title}{soon && <span className="soon-badge">Coming soon</span>}</h3>
        <span className="muted">{children}</span>
      </span>
    </div>
  );
}

function CopyText({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="copy-text">
      {text}
      <button className="icon-button" aria-label="Copy" onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked; nothing to do */ }
      }}>
        {copied ? <Icon.Check /> : <Icon.Copy />}
      </button>
    </span>
  );
}
